import { toast } from "sonner";
import { z } from "zod";

import type { Wallet } from "@/types/banks";

import { PaymentProvider } from "@/types/banks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { PaymentConfig } from "./verify-payments";

import { usePrivateAxios } from "./use-private-axios";

export interface WalletResponse {
	statusCode: number;
	data: Wallet;
	message: string;
	isSuccessful: boolean;
}

export function useViewWallet() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["view-wallet"],
		queryFn: async () => {
			const { data } = await privateAxios.get<WalletResponse>("/wallet/view_wallet");
			return data;
		}
	});
}

export function useCreateWallet() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["create-wallet"],
		mutationFn: async () => {
			const { data } = await privateAxios.post("/wallet/create_wallet");
			return data;
		}
	});
}

const fundsSchema = z.object({
	bankId: z.string().optional(),
	amount: z.coerce.number().positive("Amount must be greater than 0"),
	description: z.enum(["WITHDRAW_FUNDS", "DEPOSIT_FUND", "ORDER_SETTLEMENT", "ORDER_PAYMENT"], {
		message: "Description is required"
	}),
	paymentProvider: z.nativeEnum(PaymentProvider, { message: "Payment provider is required" })
});

interface DepositResponse {
	data: {
		deposit: {
			userId: string;
			walletId: string;
			type: "DEPOSIT" | "WITHDRAWAL";
			description: string;
			status: string;
			amount: number;
			refId: string;
			paymentProvider: PaymentProvider;
			id: string;
			createdAt: string;
		};
		paymentConfig: PaymentConfig;
	};
}

type DepositVariables = z.infer<typeof fundsSchema>;
type OnSuccessCallback = (data: DepositResponse, variables: DepositVariables) => void;

export function useCreateDesposit(OnSuccessCallback?: OnSuccessCallback) {
	const queryClient = useQueryClient();
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["create-deposit"],
		mutationFn: async (body: z.infer<typeof fundsSchema>) => {
			const { data, error } = fundsSchema.safeParse(body);
			if (error)
				throw new Error(JSON.stringify(error.flatten().fieldErrors, null, 2));

			const { data: res } = await privateAxios.post<DepositResponse>("/wallet/create_deposit", data);
			return res;
		},

		onSuccess: (data, variables) => {
			if (OnSuccessCallback)
				OnSuccessCallback(data, variables);
		},
		onError: () => toast.error("Something went wrong"),
		onSettled: (res, _, variables) => {
			if (res && variables.paymentProvider === PaymentProvider.VFD) {
				localStorage.setItem("vfd-session", res?.data.paymentConfig.accessToken ?? "");
			}
			queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
		}
	});
}

export function useCreateWithdrawal() {
	const queryClient = useQueryClient();
	const privateAxios = usePrivateAxios();

	return useMutation({
		mutationKey: ["create-deposit"],
		mutationFn: async (body: z.infer<typeof fundsSchema>) => {
			const { data, error } = fundsSchema.safeParse(body);
			if (error)
				throw new Error(JSON.stringify(error.flatten().fieldErrors, null, 2));

			const { data: res } = await privateAxios.post("/wallet/create_withdrawal", data);
			return res;
		},
		onSuccess: () => toast.success("Withdrawal request sent"),
		onError: (error: any) => {
			const errorMessage = (error.response?.data?.message as string) || error.message;
			toast.error("Something went wrong", {
				description: errorMessage
			});
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: "view-wallet" });
		}
	});
}

export interface TransactionDetails {
	id: string;
	userId: string;
	walletId: string;
	type: "DEPOSIT" | "WITHDRAWAL" | "PURCHASE";
	description: string;
	status: string;
	amount: string;
	refId: string;
	paymentProvider: string;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
	wallet: Wallet;
}

interface TransactionsResponse {
	statusCode: number;
	data: { items: TransactionDetails[] };
	message: string;
	isSuccessful: boolean;
}
export function useTransactions() {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["transactions"],
		queryFn: async () => {
			const { data } = await privateAxios.get<TransactionsResponse>("/wallet/view_user_transactions");
			return data;
		}
	});
}
export function useUpdateTransaction() {
	const queryClient = useQueryClient();
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationKey: ["update-transaction"],
		mutationFn: async (body: { transactionId: string }) => {
			const { data } = await privateAxios.post<TransactionsResponse>("/wallet/update_transaction_status", body);
			return data;
		},
		onSettled: async () => {
			queryClient.invalidateQueries({ queryKey: ["transactions"] });
			queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
		}
	});
}

export function useVerifyVfdSession() {
	const axios = usePrivateAxios();
	return useMutation({
		mutationFn: async (data: {
			transactionId: string;
			sessionId: string;
			type: "TRANSACTION" | "ORDER";
		}) => {
			const res = await axios.post("/wallet/vfd_bank_session", data);
			return res.data;
		}
	});
}
